import React from "react"
import _ from "lodash"

import { Project, CustomFeatureType } from "../../../../api/types"
import { GetApi } from "../../../../api/api"
import Button from "../../../../components/Button"
import Table, {
  AddButton,
  ActionPopover,
  ActionPopoverButton,
} from "../../../../components/Table"
import AppContext from "../../../../components/AppContext"
import { LockBadge } from "../../../../components/Badge"

import EditModal from "./components/EditModal"
import { disableIfIntroducedInPreviousVersionOrOverride } from "../../../../components/disableInputPolicy"

interface FeatureTypesProps {
  featureCatalog: Project
  customFeatureTypes: CustomFeatureType[]
  refresh: () => void
  disabled: boolean
  setDisabled: (value: boolean) => void
}

interface FeatureTypesState {
  showEditModal: boolean
  isNewFeatureType?: boolean
  editCustomFeatureType?: CustomFeatureType
  disabled: boolean
}

class FeatureTypes extends React.Component<
  FeatureTypesProps,
  FeatureTypesState
> {
  public static contextType = AppContext

  constructor(props: FeatureTypesProps) {
    super(props)
    this.state = {
      showEditModal: false,
      disabled: props.disabled,
    }
  }

  public componentDidUpdate(prevProps: FeatureTypesProps) {
    if (!_.isEqual(prevProps, this.props)) {
      this.setState({
        disabled: this.props.disabled,
      })
    }
  }

  public render() {
    const rowData = this.props.customFeatureTypes.map((cft) => {
      return {
        icon: (
          <span className="d-flex justify-content-center">
            <i className="modus-icons">volumes</i>
          </span>
        ),
        name: cft.name,
        action: (
          <this.EditButtons
            cft={cft}
            currentVersion={this.props.featureCatalog.version}
            disabled={this.state.disabled}
          />
        ),
      }
    })

    const columnData = [
      {
        Header: "",
        accessor: "icon",
        width: 70,
        sortable: false,
      },
      {
        Header: "Feature Types",
        accessor: "name",
      },
      {
        Header: (
          <AddButton
            onClick={this.createCustomFeatureType}
            disabled={this.state.disabled}
          />
        ),
        accessor: "action",
        width: 70,
        sortable: false,
      },
    ]

    return (
      <React.Fragment>
        {this.state.showEditModal && (
          <EditModal
            cftId={this.state.editCustomFeatureType!.id}
            fccId={this.props.featureCatalog.customizationId!}
            bfcId={this.props.featureCatalog.baseFeatureCatalogs[0]?.id}
            currentVersion={this.props.featureCatalog.version!}
            toggle={this.toggleEditModal}
            isOpen={this.state.showEditModal}
            headerText={
              this.state.isNewFeatureType
                ? "New Feature Type"
                : "Edit Feature Type"
            }
            toggleText={this.state.isNewFeatureType ? "Create" : "Done"}
            onCancel={
              this.state.isNewFeatureType
                ? () =>
                    this.deleteCustomFeatureType(
                      this.state.editCustomFeatureType!
                    )
                : undefined
            }
            size="xl"
          />
        )}
        {rowData.length > 0 ? (
          <Table data={rowData} columns={columnData} />
        ) : (
          <Button
            variant="primary"
            onClick={this.createCustomFeatureType}
            disabled={this.state.disabled}
          >
            New feature type
          </Button>
        )}
      </React.Fragment>
    )
  }

  private EditButtons = (props: {
    cft: CustomFeatureType
    currentVersion?: number
    disabled: boolean
  }) => {
    const editCustomFeatureType = () => this.toggleEditModal(props.cft)
    const deleteCustomFeatureType = () =>
      this.deleteCustomFeatureType(props.cft)

    return (
      <ActionPopover>
        <ActionPopoverButton
          onClick={editCustomFeatureType}
          disabled={props.disabled}
        >
          <span>Edit</span>
        </ActionPopoverButton>
        <div className="dropdown-divider" />
        <ActionPopoverButton
          variant="danger"
          onClick={deleteCustomFeatureType}
          disabled={disableIfIntroducedInPreviousVersionOrOverride(
            props.cft,
            props.currentVersion,
            props.disabled
          )}
        >
          <span>Delete</span>
          {props.cft.introducedInVersion !== props.currentVersion && (
            <LockBadge className="ml-3" variant="danger" />
          )}
        </ActionPopoverButton>
      </ActionPopover>
    )
  }

  private createCustomFeatureType = async () => {
    // this.setDisabled(true); // Issue with the panel refresh, so turn off for now
    try {
      const api = await GetApi()
      const customFeatureType = await api.createCustomFeatureType(
        this.props.featureCatalog.customizationId!,
        {
          name: "NewFeatureType",
          description: "",
        }
      )
      this.toggleEditModal(customFeatureType, true)
    } catch (error) {
      this.context.useToasts.addToast(error, "danger")
    } finally {
      this.setDisabled(false)
    }
  }

  private deleteCustomFeatureType = async (cft: CustomFeatureType) => {
    this.setDisabled(true)
    try {
      const api = await GetApi()
      if (this.state.showEditModal === true) {
        this.toggleEditModal(cft, true)
        await api.deleteCustomFeatureType(
          this.props.featureCatalog.customizationId!,
          cft.id
        )
        this.props.refresh()
      } else {
        if (
          window.confirm("Are you sure you want to delete this feature type?")
        ) {
          await api.deleteCustomFeatureType(
            this.props.featureCatalog.customizationId!,
            cft.id
          )
          this.props.refresh()
          this.context.useToasts.addToast(
            `Successfully deleted the feature type '${cft.name}'.`,
            "success",
            true
          )
        }
      }
    } catch (error) {
      this.context.useToasts.addToast(error, "danger")
    } finally {
      this.setDisabled(false)
    }
  }

  private toggleEditModal = (
    cft?: CustomFeatureType,
    isNew: boolean = false
  ) => {
    this.setState(
      (prevState) => ({
        isNewFeatureType: isNew,
        editCustomFeatureType: cft,
        showEditModal: !prevState.showEditModal,
      }),
      () => {
        if (
          this.state.showEditModal === false &&
          !this.state.isNewFeatureType
        ) {
          this.props.refresh()
        }
      }
    )
  }

  private setDisabled = (value: boolean) => {
    this.props.setDisabled(value)
    this.setState({
      disabled: value,
    })
  }
}

export default FeatureTypes
